/**
 * i18n configuration for the MSTMS admin panel.
 *
 * This module bootstraps `i18next` + `react-i18next` for the bilingual
 * English / Arabic (RTL) admin UI. It loads the two static translation
 * bundles (`./locales/en.json`, `./locales/ar.json`), wires them into a
 * single `i18n` instance, restores the user's previously chosen language
 * from `localStorage`, and exposes small helpers for switching language and
 * reading the current text direction.
 *
 * Import the default-exported `i18n` instance where you need direct access
 * (e.g. `i18n.t(...)`); inside React components prefer the `useTranslation()`
 * hook from `react-i18next`, which is powered by this same instance.
 *
 * @module i18n
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import ar from './locales/ar.json';

/**
 * The languages supported by the admin panel.
 *
 * Each entry carries the i18next language `code`, the human-readable `label`
 * shown in the language switcher (in its own script), and the text `dir`
 * ('ltr' for English, 'rtl' for Arabic) used to drive document/layout
 * direction. Declared `as const` so the codes can be derived as a literal
 * union type (see {@link LanguageCode}).
 */
export const LANGUAGES = [
  { code: 'en', label: 'English', dir: 'ltr' as const },
  { code: 'ar', label: 'العربية', dir: 'rtl' as const },
] as const;

/** Union of supported language codes ('en' | 'ar'), derived from {@link LANGUAGES}. */
export type LanguageCode = (typeof LANGUAGES)[number]['code'];

// Restore the persisted language from localStorage under the 'mstms_lang' key.
// Guarded for non-browser contexts (e.g. SSR/tests) where `window` is absent.
const savedLang = typeof window !== 'undefined' ? localStorage.getItem('mstms_lang') : null;
// Fall back to English unless a valid persisted value ('ar' or 'en') is found.
const initialLang = (savedLang === 'ar' || savedLang === 'en' ? savedLang : 'en') as LanguageCode;

// Initialize the singleton i18next instance and bind it to react-i18next so
// `useTranslation()` / `<Trans>` resolve against the bundles registered here.
i18n.use(initReactI18next).init({
  resources: { en: { translation: en }, ar: { translation: ar } },
  lng: initialLang,
  fallbackLng: 'en',
  interpolation: { 
    // SECURITY: Enable escapeValue to prevent XSS attacks
    // User-supplied values in translations will be HTML-escaped
    escapeValue: true,
  },
});

/**
 * Switch the active UI language and persist the choice.
 *
 * Calls `i18n.changeLanguage`, which re-renders all subscribed components,
 * then stores the selection in `localStorage` (`mstms_lang`) so it survives
 * reloads (see the restore logic above). Persistence is skipped in
 * non-browser contexts. Note: this does NOT itself update the document's
 * `dir`/`lang` attributes — callers that need RTL layout flipping should read
 * {@link getDirection} (e.g. on the i18n `languageChanged` event) and apply it.
 *
 * @param code - The language to switch to ('en' or 'ar').
 */
export function setLanguage(code: LanguageCode) {
  i18n.changeLanguage(code);
  if (typeof window !== 'undefined') {
    localStorage.setItem('mstms_lang', code);
  }
}

/**
 * Get the text direction for the currently active language.
 *
 * @returns 'rtl' when the active language is Arabic, otherwise 'ltr'. Useful
 * for setting `document.dir`, MUI theme `direction`, or `dir` attributes.
 */
export function getDirection(): 'ltr' | 'rtl' {
  return i18n.language === 'ar' ? 'rtl' : 'ltr';
}

/** The configured, react-i18next-bound i18next singleton used app-wide. */
export default i18n;
